/**
 * 格式化工具函数
 */
export const jufairOss2 = 'jufair.oss-cn-shanghai.aliyuncs.com'
export const dcdnurl2 = 'dcdn-jufair.jufair.com'

/**
 * 格式化浏览量
 * 当数值大于等于10000时，转为k单位显示
 * @param val 原始数值
 * @returns 格式化后的字符串
 */
export function formatViews(val: number): string {
  if (val >= 10000) return (val / 1000).toFixed(0) + 'k'
  return val.toString()
}

/**
 * 格式化大数字，适用于更多场景
 * @param val 原始数值
 * @param options 配置选项
 * @returns 格式化后的字符串
 */
export function formatNumber(
  val: number,
  options?: {
    kThreshold?: number // k单位阈值，默认10000
    decimals?: number // 小数位数，默认0
    suffix?: string // 后缀，默认为空
  }
): string {
  if (!val && val !== 0) return '0'

  const kThreshold = options?.kThreshold || 10000
  const decimals = options?.decimals !== undefined ? options.decimals : 0
  const suffix = options?.suffix || ''

  if (val >= kThreshold) {
    return (val / 1000).toFixed(decimals) + 'k' + suffix
  }

  return val.toFixed(decimals) + suffix
}

/**
 * 格式化金额
 * @param val 金额数值
 * @param currency 货币符号，默认￥
 * @param decimals 小数位数，默认2
 * @returns 格式化后的金额字符串
 */
export function formatCurrency(val: number, currency: string = '￥', decimals: number = 2): string {
  if (!val && val !== 0) return currency + '0'
  return currency + val.toFixed(decimals)
}

/**
 * 格式化面积
 * @param val 带单位的面积字符串，如 "65000㎡"
 * @returns 格式化后的字符串
 */
export function formatArea(val: string): string {
  if (!val) return '-'

  // 提取数字部分
  const num = parseInt(val.replace(/[^0-9]/g, ''))
  if (isNaN(num)) return val

  // 提取单位部分
  const unit = val.replace(/[0-9]/g, '')

  // 格式化数字
  if (num >= 10000) {
    return (num / 10000).toFixed(1) + '万' + unit
  }
  return val
}

// Cookie操作工具类，兼容Nuxt2的$cookies接口
export const $cookies = {
  get(name: string) {
    if (process.server) {
      return undefined
    }
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift()
    }
    return undefined
  },

  set(name: string, value: string, options: any = {}) {
    if (process.server) {
      return
    }

    let cookieString = `${name}=${value}`

    if (options.maxAge) {
      cookieString += `; max-age=${options.maxAge}`
    }

    if (options.expires) {
      cookieString += `; expires=${options.expires}`
    }

    if (options.domain) {
      cookieString += `; domain=${options.domain}`
    } else {
      // 默认域名逻辑，与Nuxt2保持一致
      const domainStr =
        typeof window !== 'undefined' && window.location.host.includes('.jufair.com')
          ? 'jufair.com'
          : typeof window !== 'undefined'
            ? window.location.hostname
            : ''
      if (domainStr) {
        cookieString += `; domain=${domainStr}`
      }
    }

    if (options.path) {
      cookieString += `; path=${options.path}`
    } else {
      cookieString += `; path=/`
    }

    if (options.secure) {
      cookieString += `; secure`
    }

    if (options.httpOnly) {
      cookieString += `; httponly`
    }

    if (options.sameSite) {
      cookieString += `; samesite=${options.sameSite}`
    }

    document.cookie = cookieString
  },

  remove(name: string, options: any = {}) {
    this.set(name, '', { ...options, maxAge: -1 })
  }
}
/**
 * 设置cookie - 兼容Nuxt2的handleSetCookie函数
 * @param name cookie的key
 * @param value cookie的值
 * @param days 过期时间(天数)
 * @param domain 域名
 */
export function handleSetCookie(name: string, value: string, days: number, domain: string = '') {
  if (process.server) {
    return
  }

  let domainStr = ''
  if (domain) {
    domainStr = domain
  } else {
    domainStr = window.location.host.includes('.jufair.com') ? 'jufair.com' : window.location.hostname
  }

  let expires = ''
  if (days) {
    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    expires = '; expires=' + date.toUTCString()
  }
  document.cookie = name + '=' + (value || '') + expires + '; domain=' + domainStr + '; path=/'
}

import dayjs from 'dayjs'

/**
 * 日期格式化工具函数
 */

// 格式化为 MM月DD日 格式
export const dateFormat2 = (date: string | Date) => {
  return dayjs(date).format('MM月DD日')
}

// 格式化为标准日期格式
export const formatDate = (date: string | Date, format = 'YYYY-MM-DD') => {
  return dayjs(date).format(format)
}

// 格式化为日期时间格式
export const formatDateTime = (date: string | Date, format = 'YYYY-MM-DD HH:mm:ss') => {
  return dayjs(date).format(format)
}

import ContentBg from '~/assets/images/content_bg.webp'
import defaultLogo from '~/assets/images/default-logo.webp'
import { bucketHost, bucketCdnHost } from '~/utils/common'

// 获取 Logo
export const getLogo = (pic: string) => {
  if (!pic) return defaultLogo
  try {
    const parsedPic = JSON.parse(pic)
    if (Array.isArray(parsedPic) && parsedPic.length > 0) {
      return parsedPic[0]
    }
    return pic
  } catch (e) {
    return pic || defaultLogo
  }
}

// 获取展会 Webp 图片 (使用 OSS process 参数)
export const getExhibitionWebp = (url: string) => {
  if (!url) return defaultLogo

  let finalUrl = url
  try {
    const parsedUrl = JSON.parse(url)
    if (Array.isArray(parsedUrl) && parsedUrl.length > 0) {
      finalUrl = parsedUrl[0]
    }
  } catch (e) {
    finalUrl = url
  }
  finalUrl = finalUrl.replace('http://', 'https://').replace(bucketHost, bucketCdnHost)
  if (finalUrl.includes('?')) {
    return `${finalUrl}&x-oss-process=image/format,webp`
  }
  return `${finalUrl}?x-oss-process=image/format,webp`
}

// 产业筛选
export const industryFilter = (industry: string) => {
  if (!industry) return ''
  return industry.split(',')[0]
}

// 获取进度百分比
export const getPercent = (val: number) => {
  return `${(val || 0) * 20}%`
}

export { ContentBg }

/**
 * globalMethods
 * @author wwl
 * @created 2024/10/27
 * @modified 2024/10/27
 */
export const numFormat = (str: string) => {
  const num = parseInt(str)
  if (num >= 10000) {
    return `${(num / 10000).toFixed(1)}w`
  }
  return num
}

/**
 * 富文本图片懒加载
 * @param selector - CSS选择器，用于查找包含图片的容器，默认为 '.introduction_exh'
 * @param rootMargin - 提前加载的距离，默认为 '100px'
 */
import xss from 'xss'
import { getXOssWebpSize } from './common'
export const lazyLoadHtmlImages = (selector: string = '.introduction_exh', rootMargin: string = '100px') => {
  if (!process.client) return

  // 尝试不同的选择器
  let imgs = document.querySelectorAll(`${selector} img[data-src]`)

  // 如果找不到，尝试更宽泛的选择器
  if (imgs.length === 0) {
    imgs = document.querySelectorAll('img[data-src]')
  }

  if (imgs.length === 0) {
    return
  }

  if (!('IntersectionObserver' in window)) {
    // 兼容不支持的浏览器，直接加载
    imgs.forEach(img => {
      const dataSrc = img.getAttribute('data-src')
      if (dataSrc && !(img as HTMLImageElement).src) {
        ;(img as HTMLImageElement).src = dataSrc
        img.removeAttribute('data-src')
      }
    })
    return
  }

  const observer = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement
          const dataSrc = img.getAttribute('data-src')
          if (dataSrc && !img.src) {
            img.src = dataSrc
            img.removeAttribute('data-src')
          }
          observer.unobserve(img)
        }
      })
    },
    { rootMargin }
  )

  imgs.forEach(img => {
    observer.observe(img)
  })
}
/**
 * 处理资讯内容
 * 资讯的图片src属性改为data-src(懒加载需要)
 * 如果图片域名包含jufair.com, 给图片后缀加!swebp(图片压缩)
 * data - 富文本内容
 * firstFetchpriority - 是否给第一个图片添加 fetchpriority="high"(优先加载), 默认false
 * */
export const handleNewDetail = (data: string, firstFetchpriority: boolean = false) => {
  if (!data) {
    return data
  }
  // let result = data
  //     .replace(/src=/gi, 'data-src=')
  //     .replace(/(<img.*?data-src=")(.*?)(".*?>)/gi, function (a, b, c, d) {
  //         //a是整体
  //         //b是第一个分组  <img data-src="
  //         //c是第二个分组  图片url
  //         //d是第三个分组  ">
  //         // 增加图片压缩后缀 !swebp
  //         if (c.includes('jufair.com') && !c.includes('!swebp')) {
  //             return `${b}${c}!swebp${d}`
  //         }
  //         return a
  //     })
  let imgIndex = 0

  let result = data
    .replace(/<img([^>]*?)((?:\s+)?src=)/gi, (match, beforeSrc, srcAttr) => {
      // 检查是否已经有 loading 或 fetchpriority 属性
      const hasLoading = /loading\s*=\s*["'][^"']*["']/.test(beforeSrc)
      const hasFetchPriority = /fetchpriority\s*=\s*["'][^"']*["']/.test(beforeSrc)

      const currentIndex = imgIndex
      imgIndex++

      if (!hasLoading && !hasFetchPriority) {
        // 如果 beforeSrc 不为空且不以空格结尾，添加空格
        const needSpace = beforeSrc && !/\s$/.test(beforeSrc)
        // 第一个图片加 fetchpriority="high"，其余加 loading="lazy"
        let priorityAttr = ''
        if (firstFetchpriority && currentIndex === 0) {
          priorityAttr = 'fetchpriority="high"'
        } else {
          priorityAttr = 'loading="lazy"'
        }
        return `<img${beforeSrc}${needSpace ? ' ' : ''} ${priorityAttr} ${srcAttr}`
      }
      return match
    })
    .replace(/(<img.*?src=")(.*?)(".*?>)/gi, function (a, b, c, d) {
      //a是整体
      //b是第一个分组  <img...src="
      //c是第二个分组  图片url
      //d是第三个分组  ">
      // 增加图片压缩后缀 !swebp
      if (!c.includes('!swebp')) {
        //
        c = c.replace('http://', 'https://').replace(bucketHost, bucketCdnHost).replace(jufairOss2, dcdnurl2)
        // ?x-oss-process=image/resize,w_${800},h_${1000}/format,webp/watermark,image_bG9nby9qdXpoYW4ucG5nP3gtb3NzLXByb2Nlc3M9aW1hZ2UvcmVzaXplLFBfMTI,x_10,y_10
        return `${b}${c}${getXOssWebpSize({ w: 1200, h: 0, watermark: true })}${d}`
      }
      return a
    })
  return result
}
const defaultWhite = xss.getDefaultWhiteList()
defaultWhite.img = [
  'src',
  'alt',
  'title',
  'width',
  'height',
  'loading', // 允许常规属性
  'data-src' // 关键：允许 data-src 属性用于懒加载
]

export const xssOptions = {
  whiteList: {
    ...defaultWhite,
    html: ['class', 'style'], // 允许 html 元素的 class 和 style 属性
    body: ['class', 'style'], // 允许 body 元素的 class 和 style 属性
    head: ['class', 'style'],
    content: ['class', 'style']
  }
}

import isString from 'lodash/isString'

export const autoAddLink = async (content = '', keywords: any[] = [], title = '') => {
  if (!isString(content)) return content
  const hostName = 'https://www.jufair.com'
  // 查找所有不替换的内容
  const findNotReplace = async (reg: RegExp, str: string) => {
    return await str.match(reg)
  }

  // 把不替换的内容替换成特殊字符
  const replaceNotContent = async (list: any, content: string, chart: string) => {
    await (list || []).map((item: any, index: any) => {
      content = (content ?? '') != '' ? content.replace(item, chart + index + chart) : content
    })
    return content
  }
  // 替换超链接
  const replaceLinkContent = async (list: any, content: string) => {
    await (list || []).map((item: string) => {
      const regex = /<a.*?>(.*?)<\/a>/gi
      const result = regex.exec(item)
      const temp = (result && result[1].trim()) || ''

      content = (content ?? '') != '' ? content.replace(item, temp) : content
    })
    return content
  }

  // 查找有效关键词
  const findValidKeywords = async (keywords: any[], content: string) => {
    const validKeywords: any[] = []
    await (keywords || []).map((item: { content: string | RegExp }) => {
      const reg = new RegExp(item.content)
      const validKeyword = reg.test(content)
      if (validKeyword) validKeywords.push(item)
    })
    return validKeywords
  }

  // 删除重复链接关键词，保留长尾词
  function delRepeatKerword(validKeywords: any[]) {
    for (let i = 0; i < validKeywords.length; i++) {
      for (let j = i + 1; j < validKeywords.length; j++) {
        if (validKeywords[i].url === validKeywords[j].url) {
          if (validKeywords[i].content.length <= validKeywords[j].content.length) {
            validKeywords.splice(i, 1)
          } else {
            validKeywords.splice(j, 1)
          }
        }
      }
    }
  }

  // 排序
  function quickSort(arr: any[]): any {
    // 如果数组长度小于等于1，则返回数组本身
    if (arr.length <= 1) return arr
    // 定义中间值的索引
    const index = Math.floor(arr.length / 2)
    // 取到中间值
    const temp = arr.splice(index, 1)[0]
    // 定义左右部分数组
    const left = []
    const right = []
    for (let i = 0; i < arr.length; i++) {
      // 如果元素比中间值小，那么放在左边，否则放右边
      if (arr[i].content.length > temp.content.length) {
        left.push(arr[i])
      } else {
        right.push(arr[i])
      }
    }
    return quickSort(left).concat(temp, quickSort(right))
  }

  // 替换关键词
  const replaceKeywords = ({ keywords, content, length }: { keywords: any[]; content: string; length: number }) => {
    let num = 0
    keywords.map((item: any, index: number) => {
      if (num < length) {
        if (content.includes(item.content)) {
          content = (content ?? '') != '' ? content.replace(item.content, '%' + index + '%') : content
          num++
        }
      }
    })
    return content
  }

  // 恢复不替换内容
  const restoreNotContent = async (arr: any, content: string, chart: any) => {
    await (arr || []).map((item: string, index: any) => {
      content = (content ?? '') != '' ? content.replace(chart + index + chart, item.replace('http://', '//')) : content
    })
    return content
  }
  // 恢复图片
  const restoreImg = async (arr: any, content: string, chart: string) => {
    const reg = /\bsrc\b\s*=\s*['"]?([^'"]*)['"]?/gi
    await (arr || []).map((item: string, index: any) => {
      const src = item.match(reg)
      content =
        (content ?? '') != ''
          ? content.replace(
              chart + index + chart,
              `<img ${src && src[0].replace('http://', '//')} data-${
                src && src[0].replace('http://', '//')
              } title='${title}' alt='${title}'>`
            )
          : content
    })
    return content
  }

  const replaceContent = async (list: any, content: string, chart: string) => {
    await (list || []).map((item: { url: any; content: any }, index: any) => {
      const a = `<a target="_blank" href="${hostName}${item.url}" title="${item.content}">${item.content}</a>`
      content = (content ?? '') != '' ? content.replace(chart + index + chart, a) : content
    })
    return content
  }

  try {
    const imgReg = /<img\b.*?(>|\/>)/gi
    const aReg = /<a\b.*?>.*?(\/a>)/gi

    const imgList = await findNotReplace(imgReg, content) // 查找所有图片标签
    const aList = await findNotReplace(aReg, content) // 查找所有A标签

    content = await replaceNotContent(imgList, content, '$') // 替换所有图片内容
    content = await replaceLinkContent(aList, content) // 替换所有超链接内容

    const validKey = await findValidKeywords(keywords, content) // 匹配有效的关键词
    delRepeatKerword(validKey) // 删除重复链接关键词，保留长尾词

    const sortKey = await quickSort(validKey) // 词库排序
    content = await replaceKeywords({ keywords: sortKey, content, length: 3 }) // 替换关键词
    content = await restoreImg(imgList, content, '$') // 还原图片内容
    content = await restoreNotContent(aList, content, '@') // 还原超链接内容
    content = await replaceContent(sortKey, content, '%') // 还原关键词内容
  } catch (e) {
    console.error(e)
  }
  console.log('141-links')
  return content
}

import path from 'path'

export const getLogger = async () => {
  return
  if (process.client) return null
  const { createLogger, format } = await import('winston')
  const DailyRotateFile = (await import('winston-daily-rotate-file')).default

  const customFormat = format.combine(
    format.timestamp({ format: 'YYYY-MM-DD HH:mm:ss' }),
    format.align(),
    format.printf((i: any) => `${i.level}: ${[i.timestamp]}: ${i.message}`)
  )

  // 日志存放路径
  const fullInfoPath = path.join('./logs', `%DATE%-info.log`)
  const fullErrorPath = path.join('./logs', `%DATE%-error.log`)

  const defaultOptions = {
    format: customFormat,
    datePattern: 'YYYY-MM-DD',
    zippedArchive: true
  }

  return createLogger({
    format: customFormat,
    transports: [
      new DailyRotateFile({
        filename: fullInfoPath,
        level: 'info',
        maxFiles: 1,
        maxSize: 1024 * 1024 * 1024, // 1024MB
        ...defaultOptions
      }),
      new DailyRotateFile({
        filename: fullErrorPath,
        level: 'error',
        maxSize: 5 * 1024 * 1024, // 5MB
        maxFiles: 5,
        ...defaultOptions
      })
    ]
  })
}

/** vite的特殊性, 需要处理图片 */
const require_ = (imgPath: string) => {
  try {
    const handlePath = imgPath.replace('@', '..')
    //   console.log('handlePath::', imgPath)
    return new URL(handlePath, import.meta.url).href
  } catch (error) {
    console.warn(error)
  }
}

export { require_ }

// 获取域名
export const getDomain = (url: string) => {
  try {
    return new URL(url).hostname
  } catch {
    return ''
  }
}

// element Plus 表单基础校验
export const requiredBlur = {
  required: true,
  message: '请输入',
  trigger: 'blur'
}
export const requiredChange = {
  required: true,
  message: '请选择',
  trigger: 'change'
}

/**
 * 验证电子邮箱格式
 */
export function validEmail(value: string) {
  return /^\w+((-\w+)|(\.\w+))*\@[A-Za-z0-9]+((\.|-)[A-Za-z0-9]+)*\.[A-Za-z0-9]+$/.test(value)
}

/**
 * 验证手机格式
 */
export function validMobile(value: string) {
  return /^1[3-9]\d{9}$/.test(value)
}

/**
 * 验证URL格式
 */
export function validUrl(value: string) {
  return /http(s)?:\/\/([\w-]+\.)+[\w-]+(\/[\w-.\/?%&=]*)?/.test(value)
}

/**
 * 验证日期格式
 */
export function validDate(value: string) {
  return !/Invalid|NaN/.test(new Date(value).toString())
}

/**
 * 验证ISO类型的日期格式
 */
export function validDateISO(value: string) {
  return /^\d{4}[\/\-](0?[1-9]|1[012])[\/\-](0?[1-9]|[12][0-9]|3[01])$/.test(value)
}

/**
 * 验证十进制数字
 */
export function validNumber(value: any) {
  return /^(?:-?\d+|-?\d{1,3}(?:,\d{3})+)?(?:\.\d+)?$/.test(value)
}

/**
 * 验证整数
 */
export function validDigits(value: any) {
  return /^\d+$/.test(value)
}

/**
 * 验证身份证号码
 */
export function validIdCard(value: string) {
  return /^[1-9]\d{5}[1-9]\d{3}((0\d)|(1[0-2]))(([0|1|2]\d)|3[0-1])\d{3}([0-9]|X)$/.test(value)
}

/**
 * 是否车牌号
 */
export function validCarNo(value: string) {
  // 新能源车牌
  const xreg =
    /^[京津沪渝冀豫云辽黑湘皖鲁新苏浙赣鄂桂甘晋蒙陕吉闽贵粤青藏川宁琼使领A-Z]{1}[A-Z]{1}(([0-9]{5}[DF]$)|([DF][A-HJ-NP-Z0-9][0-9]{4}$))/
  // 旧车牌
  const creg =
    /^[京津沪渝冀豫云辽黑湘皖鲁新苏浙赣鄂桂甘晋蒙陕吉闽贵粤青藏川宁琼使领A-Z]{1}[A-Z]{1}[A-HJ-NP-Z0-9]{4}[A-HJ-NP-Z0-9挂学警港澳]{1}$/
  if (value.length === 7) {
    return creg.test(value)
  } else if (value.length === 8) {
    return xreg.test(value)
  } else {
    return false
  }
}

/**
 * 金额,只允许2位小数
 */
export function validAmount(value: any) {
  //金额，只允许保留两位小数
  return /^[1-9]\d*(,\d{3})*(\.\d{1,2})?$|^0\.\d{1,2}$/.test(value)
}

/**
 * 中文
 */
export function validChinese(value: string) {
  let reg = /^[\u4e00-\u9fa5]+$/gi
  return reg.test(value)
}

/**
 * 只能输入字母
 */
export function validLetter(value: string) {
  return /^[a-zA-Z]*$/.test(value)
}

/**
 * 只能是字母或者数字
 */
export function validEnOrNum(value: string) {
  //英文或者数字
  let reg = /^[0-9a-zA-Z]*$/g
  return reg.test(value)
}

/**
 * 验证是否包含某个值
 */
export function validContains(value: string, param: any) {
  return value.indexOf(param) >= 0
}

/**
 * 验证一个值范围[min, max]
 */
export function validRange(value: any, param: any) {
  return value >= param[0] && value <= param[1]
}

/**
 * 验证一个长度范围[min, max]
 */
export function validRangeLength(value: any, param: any) {
  return value.length >= param[0] && value.length <= param[1]
}

/**
 * 是否固定电话
 */
export function validLandline(value: any) {
  let reg = /^\d{3,4}-\d{7,8}(-\d{3,4})?$/
  return reg.test(value)
}

/**
 * 判断是否为空
 */
export function validEmpty(value: any) {
  switch (typeof value) {
    case 'undefined':
      return true
    case 'string':
      if (value.replace(/(^[ \t\n\r]*)|([ \t\n\r]*$)/g, '').length == 0) return true
      break
    case 'boolean':
      if (!value) return true
      break
    case 'number':
      if (0 === value || isNaN(value)) return true
      break
    case 'object':
      if (null === value || value.length === 0) return true
      for (var i in value) {
        return false
      }
      return true
  }
  return false
}

/**
 * 是否json字符串
 */
export function validJsonString(value: any) {
  if (typeof value == 'string') {
    try {
      var obj = JSON.parse(value)
      if (typeof obj == 'object' && obj) {
        return true
      } else {
        return false
      }
    } catch (e) {
      return false
    }
  }
  return false
}

/**
 * 是否数组
 */
export function validArray(value: any) {
  if (typeof Array.isArray === 'function') {
    return Array.isArray(value)
  } else {
    return Object.prototype.toString.call(value) === '[object Array]'
  }
}

/**
 * 是否对象
 */
export function validObject(value: any) {
  return Object.prototype.toString.call(value) === '[object Object]'
}

/**
 * 是否短信验证码
 */
export function validCode(value: any, len = 6) {
  return new RegExp(`^\\d{${len}}$`).test(value)
}

/**
 * 验证身份证号码
 */
export function validIDCard(str: string) {
  // return /^[1-9]\d{5}[1-9]\d{3}((0\d)|(1[0-2]))(([0|1|2]\d)|3[0-1])\d{3}([0-9]|X)$/.test(
  // 	str)
  let checkResult = true
  let card_18 = /^[1-9]\d{5}(18|19|([23]\d))\d{2}((0[1-9])|(10|11|12))(([0-2][1-9])|10|20|30|31)\d{3}[0-9Xx]$/
  let card_15 = /^[1-9]\d{5}\d{2}((0[1-9])|(10|11|12))(([0-2][1-9])|10|20|30|31)\d{2}[0-9Xx]$/
  if (!card_18.test(str) && !card_15.test(str)) {
    return (checkResult = false)
  } else {
    let cardArr: any = str.split('')
    let factor = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2]
    let parity = [1, 0, 'X', 9, 8, 7, 6, 5, 4, 3, 2]
    let sum: any = 0
    for (let i = 0; i < 17; i++) {
      // console.log(cardArr[i], factor[i])
      sum += cardArr[i] * factor[i]
    }
    if (cardArr[17] != parity[sum % 11]) {
      if (cardArr[17] == 'x') {
        // Toast('小写x通过')
      } else {
        // return [false, '身份证号码输入有误'];
        checkResult = false
      }
    } else {
      // Toast('通过')
    }
    // return false
  }
  return checkResult
}
